import { useMemo, useState } from "react";
import type { EventDto } from "@mmm/contracts";
import { MonthGrid } from "../../components/calendar/MonthGrid.js";
import { EventForm, type EventFormValues } from "../../components/calendar/EventForm.js";
import { EventTypeLegend } from "../../components/calendar/EventTypeLegend.js";
import { createEvent, updateEvent, deleteEvent } from "../../api/commands.js";
import { useSubjectContext } from "./useSubjectContext.js";

export function SubjectCalendarTab() {
  const { subject } = useSubjectContext();
  const today = new Date();
  const [monthDate, setMonthDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const selectedEvent = subject.events.find((event) => event.id === selectedEventId) || null;

  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventDto[]>();
    subject.events.forEach((event) => {
      const list = map.get(event.startDate) || [];
      list.push(event);
      map.set(event.startDate, list);
    });
    return map;
  }, [subject.events]);

  const changeMonth = (amount: number) => {
    setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
  };

  const handleSave = async (values: EventFormValues) => {
    // EventForm's own onSubmit awaits and catches this — a failure here
    // stops before setSelectedEventId, so the form stays showing the event
    // being edited instead of wrongly clearing the selection.
    if (selectedEvent) {
      await updateEvent({ eventId: selectedEvent.id, ...values });
    } else {
      await createEvent({ subjectId: subject.id, ...values });
    }
    setSelectedEventId(null);
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    // EventForm's Delete button invokes this without awaiting it — a failure
    // is already toasted by commands.ts's run(); catch it here so it doesn't
    // escape unhandled, and keep the selection so the form doesn't clear.
    try {
      await deleteEvent({ eventId: selectedEvent.id });
      setSelectedEventId(null);
    } catch {
      // Nothing left to do.
    }
  };

  return (
    <div className="mmm-calendar-page">
      <EventForm
        key={selectedEventId || "new"}
        selectedEvent={selectedEvent}
        onSave={handleSave}
        onDelete={handleDelete}
        onClear={() => setSelectedEventId(null)}
      />
      <MonthGrid
        year={monthDate.getFullYear()}
        month={monthDate.getMonth()}
        onMonthChange={changeMonth}
        eventsByDate={eventsByDate}
        onEventClick={(event) => setSelectedEventId(event.id)}
      />
      <div className="mmm-calendar-legend-bar">
        <EventTypeLegend />
      </div>
    </div>
  );
}
